import { useEffect, useRef, useState } from "react"
import { Input } from "antd"
import {
	parseDimensionText,
	resolveDimensionError,
} from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/dimensionInput.helper"
import style from "./DimensionField.module.scss"

const { field, labelRow, label, requiredMark, textInput, error, hint } = style

type Props = {
	label: string
	value: number | null
	onChange: (value: number | null) => void
	onCommit?: () => void
	isRequired: boolean
	min: number
	placeholder?: string
	/** The `n LnFt` line under Width. Nothing else passes one. */
	hint?: string | null
}

/**
 * One dimension axis (issue #1088).
 *
 * A plain text `Input` rather than an `InputNumber`: Ant rewrites an out-of-range or fractional
 * entry on its way in (`1,4` → `14`, `fff` → `0`), and #1088 asks for the typed text to survive
 * with a validation message instead. See `dimensionInput.helper.ts` for the rules, and
 * `TimesReferenced` for the same decision taken for REQ-423's field.
 *
 * Ant's `Input` is used directly, as the `InputNumber` it replaces was, rather than the shared
 * `components/ui/inputs/Input` wrapper: the axis needs the `in` suffix and an `aria-label` (three
 * identical boxes sit side by side), neither of which that wrapper exposes, and it would also apply
 * a theme the previous control did not have.
 *
 * The **string** is the state, and the numeric prop is only an input to it — an invalid entry has no
 * number to round-trip through the parent, so a resync driven straight off `value` would wipe the
 * text being complained about. `publishedRef` is what tells "the parent is echoing what we just
 * sent it" (ignore) apart from "the parent has a genuinely different value" (reseed, e.g. the object
 * selection changed or the server normalised the saved number).
 */
export const DimensionField = ({
	label: labelText,
	value,
	onChange,
	onCommit,
	isRequired,
	min,
	placeholder,
	hint: hintText,
}: Props) => {
	const [text, setText] = useState(value === null ? "" : String(value))
	const [isTouched, setIsTouched] = useState(false)
	const publishedRef = useRef<number | null>(value)

	useEffect(() => {
		if (value === publishedRef.current) return
		publishedRef.current = value
		setText(value === null ? "" : String(value))
		setIsTouched(false)
	}, [value])

	const errorMessage = resolveDimensionError({ text, min, isRequired, isTouched })

	const handleChange = (raw: string) => {
		setText(raw)
		setIsTouched(true)
		const parsed = parseDimensionText(raw)
		// Anything the endpoint would reject is published as `null`, so the parent can never hold a
		// stale valid number while the field on screen shows something else.
		const next = parsed !== null && parsed >= min ? parsed : null
		publishedRef.current = next
		onChange(next)
	}

	// Nothing is sent while the field is invalid or empty — the whole point of #1088. A dimension is a
	// non-nullable integer on every endpoint, so a cleared field has nothing to commit either.
	const handleCommit = () => {
		if (errorMessage !== null || text.trim() === "") return
		onCommit?.()
	}

	return (
		<div className={field}>
			<div className={labelRow}>
				<span className={label}>{labelText}</span>
				{isRequired ? <span className={requiredMark}>*</span> : null}
			</div>
			<Input
				className={textInput}
				value={text}
				onChange={(event) => handleChange(event.target.value)}
				onBlur={handleCommit}
				onPressEnter={handleCommit}
				placeholder={placeholder}
				suffix="in"
				status={errorMessage === null ? undefined : "error"}
				aria-label={labelText}
				size="middle"
			/>
			{errorMessage === null ? null : <span className={error}>{errorMessage}</span>}
			{hintText ? <span className={hint}>{hintText}</span> : null}
		</div>
	)
}
